/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */
import React, { Component } from 'react';
import '../scss/carousel.scss';
import PropTypes from 'prop-types';
import {
    CARD_MAPPING,
    DEFAULT_SLIDE_HEIGHT,
    LEFT_BUTTON_CLICKED,
    RIGHT_BUTTON_CLICKED,
} from './constants';

const canUseDOM = !!(
    typeof window !== 'undefined'
    && window.document
    && window.document.createElement);

/* eslint-disable react/sort-comp */
export default class Carousel extends Component {
    static displayName = 'Carousel';

    tabFromIndex;

    static defaultProps = {
        actionEvents: () => { },
        cardType: 'GENERIC',
        cardSize: 'M',
        slideWidth: 0,
        slideHeight: 0,
        keepWidth: false,
        decoratorTop: -1,
        showPagerDot: false,
        metadata: {},
        customCardMapping: {},
        onCarouselClick: () => {},
        skipAccessibility: false,
    };

    static propTypes = {
        /**
         * To add callback for Left and Right Slider events
         */
        actionEvents: PropTypes.func,

        /**
         * To define cardMapping, which signifies the number of slides
         * on a page for multiple breakpoints
         */
        cardType: PropTypes.string,

        /**
         * Can be equal to 'L', 'M' or 'S'. Handles the size of cards on
         * different breakpoints. Makes no difference as of now.
         */
        cardSize: PropTypes.string,

        /**
         * Object to specify cards per row for different
         * breakpoints. Refer ./constants.js in code
         * for reference.
         */
        customCardMapping: PropTypes.object,

        /**
         * To specify width of children
         */
        slideWidth: PropTypes.number,

        /*
         * To specify height of children
         */
        slideHeight: PropTypes.number,

        /**
         * To keep the original width of a card/slide
         * such that it occupies the entire frame
         */
        keepWidth: PropTypes.bool,

        /**
         * To position the left and right sliders.
         */
        decoratorTop: PropTypes.number,

        /**
         * To add slide navigation functionality
         */
        showPagerDot: PropTypes.bool,

        /**
         *
         */
        metadata: PropTypes.object,

        /**
         * Method to be invoked when action is taken on carousel buttons/dots
         */

        onCarouselClick: PropTypes.func,

        /**
         * To make pager dots and left/right carousel arrows
         * not tabbable
         */

        skipAccessibility: PropTypes.bool,
    };

    /** *****************************************************
     * React live cycle
     ****************************************************** */

    // Touch Events

    touchObject = {};

    constructor(props) {
        super(props);
        const { customCardMapping } = this.props;
        const cardMapping = { ...CARD_MAPPING, ...customCardMapping };
        // one of state variables left or top can be removed but not both
        this.state = {
            currentPage: 0,
            pager: [],
            slideWidth: props.slideWidth,
            slideHeight: props.slideHeight,
            cardMapping,
            breakpoint: this.getCurrentBreakpoint(),
        };
    }

    componentWillMount() {
        this.setInitialDimensions();
    }

    componentDidMount() {
        this.bindEvents();
        setTimeout(() => {
            this.setState({
                breakpoint: this.getCurrentBreakpoint(),
            }, () => {
                this.updatePager();
                this.setDimensions();
            });
        }, 500);
    }

    componentWillReceiveProps(nextProps) {
        const { slideHeight } = this.state;
        this.setState({
            slideHeight: nextProps.slideHeight > 0 ? nextProps.slideHeight : slideHeight,
        });
        this.setDimensions();
    }

    componentWillUnmount() {
        this.unbindEvents();
    }

    getCurrentBreakpoint() {
        let currentBreakpoint = 'M';
        const windowWidth = window.innerWidth && document.documentElement.clientWidth
            ? Math.min(window.innerWidth, document.documentElement.clientWidth)
            : window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth;

        if (windowWidth >= 1768) {
            currentBreakpoint = 'L';
        } else if (windowWidth >= 1440) {
            currentBreakpoint = 'LM';
        } else if (windowWidth >= 1280) {
            currentBreakpoint = 'M';
        } else if (windowWidth >= 960) {
            currentBreakpoint = 'MS';
        } else if (windowWidth >= 760) {
            currentBreakpoint = 'S';
        } else if (windowWidth >= 480) {
            currentBreakpoint = 'SS';
        } else {
            currentBreakpoint = 'XS';
        }

        return currentBreakpoint;
    }

    onReadyStateChange = () => {
        this.setDimensions();
    };

    onResize = () => {
        if (this.frameWidth() > 0) {
            this.setState({ breakpoint: this.getCurrentBreakpoint() });
            this.setDimensions();
            this.moveToPage(this.state.currentPage);
        }
    };

    onFocus = (e, index) => {
        // Do not move if pager is on or not triggered by Tab key or only have 1 card per row

        if (this.state.showPagerDot || this.tabFromIndex === undefined) {
            return;
        }
        this.tabFromIndex = undefined;

        // prevent cards move out of range
        let pageShoudMoveTo = 0;
        const lastPage = this.state.pager.length - 1;
        this.state.pager.forEach((pageIndex, pagerIndex) => {
            if (index >= pageIndex
                && (index < this.state.pager[pagerIndex + 1] || pagerIndex === lastPage)) {
                pageShoudMoveTo = pagerIndex;
            }
        });

        this.moveToPage(pageShoudMoveTo);

        // if (index <= this.state.pager[this.state.pager.length - 1] // not the last pageIndex
        //  && index < this.state.pager[pagerIndex + 1]) {
        // current index is in this pageIndex and next pageIndex
        if ((pageShoudMoveTo < lastPage) && this.list) {
            // move the carousel to the focusing item
            const value = this.slidePosition(index);
            this.list.style.transform = `translate3d(-${value}px, 0px, 0px)`;
            this.list.style.WebkitTransform = `translate3d(-${value}px, 0px, 0px)`;
        }
    }

    addEvent = (elem, type, eventHandle) => {
        if (elem === null || typeof elem === 'undefined') {
            return;
        }
        if (elem.addEventListener) {
            elem.addEventListener(type, eventHandle, false);
        } else if (elem.attachEvent) {
            elem.attachEvent(`on ${type}`, eventHandle);
        } else {
            const element = elem;
            element[`on ${type}`] = eventHandle;
        }
    };

    removeEvent = (elem, type, eventHandle) => {
        if (elem === null || typeof elem === 'undefined') {
            return;
        }
        if (elem.removeEventListener) {
            elem.removeEventListener(type, eventHandle, false);
        } else if (elem.detachEvent) {
            elem.detachEvent(`on ${type}`, eventHandle);
        } else {
            const element = elem;
            element[`on ${type}`] = null;
        }
    };

    previousPage = () => {
        const { currentPage } = this.state;
        if (currentPage > 0) {
            this.props.onCarouselClick({
                triggerType: 'prev-nav-button',
                currIndex: currentPage,
                targetIndex: currentPage - 1,
            });
            this.moveToPage(currentPage - 1);
        }
    };

    nextPage = () => {
        const { currentPage, pager } = this.state;
        if (currentPage + 1 < pager.length) {
            this.props.onCarouselClick({
                triggerType: 'next-nav-button',
                currIndex: currentPage,
                targetIndex: currentPage + 1,
            });
            this.moveToPage(currentPage + 1);
        }
    };

    cardsPerRow() {
        const { cardSize } = this.props;
        const { cardMapping, breakpoint } = this.state;
        if (cardMapping[`${cardSize}`][`breakpoint${breakpoint}`]) {
            return cardMapping[`${cardSize}`][`breakpoint${breakpoint}`].cardsPerRow;
        }
        return 1;
    }

    cardWidth() {
        let cardWidth = 0;
        cardWidth = (this.frameWidth() - (this.cardGutter() * (this.cardsPerRow() - 1)))
            / this.cardsPerRow();
        return cardWidth;
    }

    cardWidthAtIndex(index) {
        const { keepWidth } = this.props;
        if (keepWidth) {
            const childrenWidth = Array.from(this.list.children).map(
                child => child.offsetWidth
            );
            return childrenWidth[index];
        }
        return this.cardWidth();
    }

    /**
     * [_debounce - ultility function to reduce the continously calling functions]
     */
    _debounce = (fn, time) => {
        let timeout;

        return (...theArgs) => {
            const functionCall = () => fn.apply(this, theArgs);
            clearTimeout(timeout);
            timeout = setTimeout(functionCall, time);
        };
    }

    detectTab = (e, index) => {
        this.tabFromIndex = undefined;
        const movableKeyPressed = e.key === 'Tab' || e.which === 9 || e.key === 'ArrowRight' || e.which === 39 || e.key === 'ArrowLeft' || e.which === 37;

        if (movableKeyPressed && this.cardsPerRow() !== 1) {
            this.tabFromIndex = index;
        }

        const rightArrowPressed = e.key === 'ArrowRight' || e.which === 39;
        if (rightArrowPressed) {
            if (this.list.children[index + 1]) {
                const focusable = [].slice.call(document.querySelectorAll('.carousel [tabindex]:not([tabindex="-1"])'));
                const currentFocusableIndex = focusable.indexOf(e.target);
                focusable[currentFocusableIndex + 1].focus();
                return;
            }
        }

        const leftArrowPressed = e.key === 'ArrowLeft' || e.which === 37;
        if (leftArrowPressed) {
            if (this.list.children[index - 1]) {
                const focusable = [].slice.call(document.querySelectorAll('.carousel [tabindex]:not([tabindex="-1"])'));
                const currentFocusableIndex = focusable.indexOf(e.target);
                focusable[currentFocusableIndex - 1].focus();
            }
        }
    }

    forceCarouselResize = (e) => {
        this.setDimensions();
        this.moveToPage(e.detail.index ? e.detail.index : this.state.currentPage);
    }

    formatChildren(children) {
        const self = this;
        return React.Children.map(children, (child, index) => (
            /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
            <li
                className="carousel-item"
                style={self.getSlideStyles(index)}
                key={`carousel-item-${index}`} // eslint-disable-line
                tabIndex={-1}
                onKeyDown={e => this.detectTab(e, index)}
                onFocus={e => this.onFocus(e, index)}>
                {child}
            </li>
            /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
        ));
    }

    /** *****************************************************
     * Handlers
     ****************************************************** */

    getDecoratorWrapperStyles() {
        const { decoratorTop } = this.props;
        return {
            top: (decoratorTop > -1) ? `${decoratorTop + 16}px` : '50%',
        };
    }

    getDecoratorStyles(disabled) {
        return {
            display: disabled ? 'none' : 'block',
            cursor: 'pointer',
        };
    }

    getTouchEvents() {
        const self = this;

        return {
            onTouchStart(e) {
                self.touchObject = {
                    startX: e.touches[0].pageX,
                    startY: e.touches[0].pageY,
                };
            },
            onTouchMove(e) {
                const direction = self.swipeDirection(
                    self.touchObject.startX,
                    e.touches[0].pageX,
                    self.touchObject.startY,
                    e.touches[0].pageY
                );

                const distance = Math.round(Math.sqrt(
                    (e.touches[0].pageX - self.touchObject.startX) ** 2
                ));

                self.touchObject = {
                    startX: self.touchObject.startX,
                    startY: self.touchObject.startY,
                    endX: e.touches[0].pageX,
                    endY: e.touches[0].pageY,
                    distance,
                    direction,
                };

                if (direction !== 0 && distance > 44) {
                    e.preventDefault();
                }
            },
            onTouchEnd() {
                self.handleSwipe();
            },
            onTouchCancel() {
                self.handleSwipe();
            },
        };
    }

    handleDecoratorKeyDown = (e) => {
        if (e.key === ' ' || e.key === 'Enter' || e.which === 13 || e.which === 32) {
            const button = e.currentTarget.getElementsByTagName('button')[0];
            if (button) {
                button.click();
            }
        }
    }

    handleDecorator = (e) => {
        e.preventDefault();

        if (e.currentTarget.className.indexOf('carousel-left-button') !== -1) {
            this.previousPage();
            const event = {
                type: LEFT_BUTTON_CLICKED,
                metadata: this.props.metadata,
            };

            this.props.actionEvents(event);
        } else if (e.currentTarget.className.indexOf('carousel-right-button') !== -1) {
            this.nextPage();
            const event = {
                type: RIGHT_BUTTON_CLICKED,
                metadata: this.props.metadata,
            };

            this.props.actionEvents(event);
        }
    };

    /**
     * [moveToPage - Navigate to the indicate page]
     * @param {Number} index - Index of the page we want to navigate to
     */
    moveToPage(index) {
        let value;
        const { children } = this.props;
        const { pager } = this.state;
        const lastIndex = children.length - 1;
        if (index === lastIndex || pager.length - 1 === index) {
            const listWidth = this.slidePosition(lastIndex) + this.cardWidthAtIndex(index);
            value = listWidth - this.frameWidth();
        } else {
            value = this.slidePosition(pager[index]);
        }

        if (value !== undefined && this.list) {
            this.list.style.transform = `translate3d(-${value}px, 0px, 0px)`;
            this.list.style.WebkitTransform = `translate3d(-${value}px, 0px, 0px)`;
            this.setState({ currentPage: index });
        }
    }

    /**
     * cardGutter
     *
     * @param {*} index (Optional)
     * @returns  the gutter spacing next to the card
     * @memberof Carousel
     */
    cardGutter(index) {
        const { cardSize } = this.props;
        const { cardMapping, breakpoint } = this.state;
        // If the index is the card at the most right, gutter should be 0
        if (cardMapping[`${cardSize}`][`breakpoint${breakpoint}`]
            && (((typeof index !== 'undefined' ? index : 0) + 1) % this.cardsPerRow() !== 0 || this.cardsPerRow !== 1)) {
            return cardMapping[`${cardSize}`][`breakpoint${breakpoint}`].gutter;
        }

        return 0;
    }

    // Bootstrapping

    debouncedOnResize;

    bindEvents() {
        const self = this;
        self.debouncedOnResize = self._debounce(() => self.onResize(), 100); // eslint-disable-line
        if (canUseDOM) {
            const resizeEventElement = window;
            this.addEvent(resizeEventElement, 'resize', self.debouncedOnResize);
            this.addEvent(document, 'readystatechange', self.onReadyStateChange);
            this.addEvent(window, 'forceCarouselResize', self.forceCarouselResize);
        }
    }

    renderDot() {
        return React.createElement(
            'svg',
            { viewBox: '0 0 8 8', focusable: 'false' },
            React.createElement('circle', { cx: '4', cy: '4', r: '4' })
        );
    }

    onCarouselDotClick(index) {
        const { onCarouselClick } = this.props;
        const { currentPage } = this.state;
        onCarouselClick({
            triggerType: 'dot',
            currIndex: currentPage,
            targetIndex: index,
        });
        this.moveToPage(index);
    }

    renderPagerDot() {
        const { pager, currentPage } = this.state;
        const { skipAccessibility } = this.props;
        return (
            <div className="carousel-pager">
                {pager.map((page, index) => (
                    <button
                        type="button"
                        key={`pager-dot-${pager[index]}`}
                        className={currentPage === index ? 'carousel-pager-dot active' : 'carousel-pager-dot'}
                        onClick={() => this.onCarouselDotClick(index)}
                        onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.keyCode === 13) {
                                this.moveToPage(index);
                            }
                        }}
                        aria-label={index}
                        tabIndex={skipAccessibility ? -1 : 0}>
                        {this.renderDot()}
                    </button>
                ))}
            </div>
        );
    }

    render() {
        const { showPagerDot, skipAccessibility } = this.props;
        const { currentPage, pager } = this.state;
        const children = this.formatChildren(this.props.children); // eslint-disable-line

        return (
            <div className="carousel" ref={(element) => { this.slider = element; }}>
                <div
                    className="carousel-frame"
                    ref={(element) => { this.frame = element; }}
                    {...this.getTouchEvents()}>
                    <ul className="carousel-list" ref={(element) => { this.list = element; }} style={this.getListStyles()}>
                        {children}
                    </ul>
                </div>
                <div className="carousel-left-decorator" style={this.getDecoratorWrapperStyles()}>
                    <button
                        type="button"
                        className="carousel-left-button"
                        aria-label="Previous"
                        tabIndex={skipAccessibility ? -1 : 0}
                        style={this.getDecoratorStyles(currentPage <= 0)}
                        onKeyPress={this.handleDecoratorKeyDown}
                        onClick={this.handleDecorator} />
                </div>
                <div className="carousel-right-decorator" style={this.getDecoratorWrapperStyles()} onKeyDown={this.handleDecoratorKeyDown}>
                    <button
                        type="button"
                        className="carousel-right-button"
                        aria-label="Next"
                        tabIndex={skipAccessibility ? -1 : 0}
                        style={this.getDecoratorStyles(
                            currentPage >= (pager.length - 1)
                        )}
                        onKeyPress={this.handleDecoratorKeyDown}
                        onClick={this.handleDecorator} />
                </div>
                {showPagerDot && pager.length > 1 ? this.renderPagerDot() : ''}
            </div>
        );
    }

    unbindEvents() {
        const self = this;
        if (canUseDOM) {
            const resizeEventElement = window;
            this.removeEvent(resizeEventElement, 'resize', self.debouncedOnResize);
            this.removeEvent(document, 'readystatechange', self.onReadyStateChange);
            this.removeEvent(window, 'forceCarouselResize', self.forceCarouselResize);
        }
    }

    setInitialDimensions() {
        const { children, slideHeight } = this.props;
        this.setState(
            {
                slideHeight: slideHeight > 0 ? slideHeight
                    : DEFAULT_SLIDE_HEIGHT,
            },
        );
    }

    setDimensions() {
        const self = this;
        if (self.frame && self.frameWidth() > 0) {
            self.updatePager();
            let slideHeight = 0;
            const firstSlide = self.frame.childNodes[0].childNodes[0];

            if (self.props.slideHeight > 0) {
                slideHeight = self.props.slideHeight; // eslint-disable-line
            } else if (firstSlide) {
                firstSlide.style.height = 'auto';
                slideHeight = firstSlide.offsetHeight;
            } else {
                slideHeight = DEFAULT_SLIDE_HEIGHT;
            }

            this.setState(
                {
                    slideHeight,
                    slideWidth: (self.props.slideWidth === 0 ? self.cardWidth()
                        : self.props.slideWidth),
                    cellSpacing: self.cardGutter(),
                }
            );
        }
    }

    // Styles

    getListStyles() {
        return {
            transform: 'translate3d(0px, 0px, 0px)',
            WebkitTransform: 'translate3d(0px, 0px, 0px)',
            transition: 'transform .5s',
            WebkitTransition: 'transform .5s',
            position: 'relative',
            display: 'block',
            padding: 0,
            // minHeight: `${this.state.slideHeight}px`,
            width: '100%',
            boxSizing: 'border-box',
        };
    }

    getSlideStyles(index) {
        const { keepWidth } = this.props;
        const { cellSpacing, slideWidth } = this.state;
        if (!keepWidth) {
            return {
                position: 'absolute',
                left: this.slidePosition(index),
                top: 0,
                display: 'inline-block',
                listStyleType: 'none',
                verticalAlign: 'top',
                width: slideWidth,
                height: 'auto',
                boxSizing: 'border-box',
                padding: '2px',
                marginRight: cellSpacing,
            };
        }
        return {
            position: 'relative',
            display: 'inline-block',
            listStyleType: 'none',
            verticalAlign: 'top',
            boxSizing: 'border-box',
        };
    }

    /**
     * [slidePosition - Calculate the position (px) of an item]
     * @param {Number} index - Index of the item
     */
    slidePosition(index) {
        const { keepWidth } = this.props;
        if (keepWidth) {
            const childrenWidth = Array.from(this.list.children).map(
                child => child.offsetWidth
            );
            let position = 0;
            for (let i = 0; i < index; i += 1) {
                position += childrenWidth[i];
            }
            return position;
        }

        const { slideWidth, cellSpacing } = this.state;
        if (slideWidth && cellSpacing) {
            return (slideWidth + cellSpacing) * index;
        }

        return 0;
    }

    frameWidth() {
        if (this.frame) {
            return this.frame.offsetWidth;
        }
        return 0;
    }

    // Action Methods

    /**
     * [updatePager - Update the pager array]
     */
    updatePager() {
        if (!this.list) {
            return;
        }
        const { children, keepWidth } = this.props;
        const slides = children;
        const frameWidth = this.frameWidth();
        let limit = frameWidth;
        let offsetLeft = 0;
        const pager = [0];

        for (let i = 0; i < slides.length; i += 1) {
            const itemLeft = offsetLeft - this.list.offsetLeft;
            const totalCardWidth = keepWidth ? this.cardWidthAtIndex(i)
                : this.cardWidth() + this.cardGutter(i);
            offsetLeft += totalCardWidth;

            if (Math.floor((itemLeft + totalCardWidth)) > limit + totalCardWidth / 4) {
                pager.push(i);
                limit = itemLeft + frameWidth;
            }
        }
        this.setState({ pager }, () => {
            const { currentPage } = this.state;
            if (pager.length === 1) {
                this.list.style.transform = 'translate3d(0px, 0px, 0px)';
                this.list.style.WebkitTransform = 'translate3d(0px, 0px, 0px)';
                this.setState({ currentPage: 0 });
            } else if (pager.length >= currentPage
                && currentPage > pager.length / 2) {
                this.moveToPage(this.state.pager.length - 1); // eslint-disable-line
            }
        });
    }

    swipeDirection(x1, x2, y1, y2) {
        const xDist = x1 - x2;
        const yDist = y1 - y2;
        const r = Math.atan2(yDist, xDist);

        let swipeAngle = Math.round(r * 180 / Math.PI);
        if (swipeAngle < 0) {
            swipeAngle = 360 - Math.abs(swipeAngle);
        }
        if (swipeAngle <= 45 && swipeAngle >= 0) {
            return 1;
        }
        if (swipeAngle <= 360 && swipeAngle >= 315) {
            return 1;
        }
        if (swipeAngle >= 135 && swipeAngle <= 225) {
            return -1;
        }
        return 0;
    }

    handleSwipe() {
        if (this.touchObject.distance > 44) {
            if (this.touchObject.direction === 1) {
                this.nextPage();
            } else if (this.touchObject.direction === -1) {
                this.previousPage();
            }
        }
        this.touchObject = {};
    }
}
/* eslint-enable react/sort-comp */
